import {
    AreaMode, LandAreaUnitTypeNameToId, DEFAULT_EXPENSE_ANNUAL_GROWTH, OPERATING_EXPENSES,
} from 'views/ProFormaWizard/constants';
import { IProFormaWizardAssumption } from 'views/ProFormaWizard/types';
import { IGrossBuildableAreaLot } from 'views/ProFormaTable/types';
import { PRO_FORMA_STATUS } from 'views/ProFormaHome/constants';
import { DEVELOPMENT_SOURCE_METRIC } from 'constants/sources';
import { StepOneValues } from '../components/ProFormaWizardStepOne/ProFormaWizardStepOne';
import { StepTwoValues } from '../components/ProFormaWizardStepTwo/ProFormaWizardStepTwo';

export function formatStepOneData(
    formValues: StepOneValues,
    assumptions?: IProFormaWizardAssumption,
    lots?: IGrossBuildableAreaLot[],
): any {
    return {
        ...formValues,
        areaMode: formValues.isFAR ? AreaMode.FAR : AreaMode.GROSS_BUILD_AREA,
        landAreaUnitTypeId: LandAreaUnitTypeNameToId[formValues.landAreaUnit],
        assumptions: {
            dispositionId: assumptions?.id,
            currentMarketCapRate: formValues.currentMarketCapRate,
            projectedCapRateAtSale: formValues.projectedCapRateAtSale,
            sellingCosts: formValues.sellingCosts,
        },
        grossBuildableAreaLots: formValues.calculateArea ? lots : undefined,
        calculateArea: formValues.calculateArea,
        averageStories: formValues.stories,
        draftStep: 1,
    };
}

export function formatStepTwoData(
    formValues: StepTwoValues,
): any {
    const updatedSources = formValues.sources ? formValues.sources.map(source => {
        const {
            amount,
            ltcAmount,
            calculateDebt,
            metricType,
        } = source;

        const rawAmount = calculateDebt && metricType === DEVELOPMENT_SOURCE_METRIC.LTC
            ? Number(ltcAmount)
            : amount;

        const sanitizedAmount = Number.isNaN(rawAmount) ? 0 : Math.max(rawAmount, 0);

        return {
            ...source,
            amount: sanitizedAmount,
        };
    }) : formValues;

    const updatedFormValues = { ...formValues, sources: updatedSources as any };

    return {
        ...updatedFormValues,
        draftStep: 2,
    };
}

export function formatStepThreeData({
    operatingInfoValues,
    unitArray,
    expensesDetail,
    incomeArray,
    expensesValues,
    showExpenseDetailToggle,
    showOtherIncomeToggle,
    isDraft,
}: {
    operatingInfoValues: any;
    unitArray: any[];
    expensesDetail: any[];
    incomeArray: any[];
    expensesValues: any;
    showExpenseDetailToggle: boolean;
    showOtherIncomeToggle: boolean;
    isDraft?: boolean;
}): any {
    const occupancyUponStabilizationPercentage = operatingInfoValues?.occupancyUponStabilizationPercentage || 0;

    const transformedUnitArray = unitArray.map(unit => ({
        ...unit,
        averageSquareFootage: unit.averageSquareFootage,
        valuePerUnitPerMonth: unit.amountPerMonth,
        units: unit.units,
    }));

    const transformedExpenses = expensesDetail.map(expense => ({
        ...expense,
        annualGrowth: (Number(expense.annualGrowth) || DEFAULT_EXPENSE_ANNUAL_GROWTH),
    }));

    const transformedOtherIncome = incomeArray.map(income => ({
        ...income,
        annualGrowth: Number(income.annualGrowth) || 0,
        percentageOfTotalRentalIncome: Number(income.percentageOfTotalRentalIncome) || 0,
    }));

    const operatingExpenses = showExpenseDetailToggle ? transformedExpenses : [{
        ...expensesValues, id: 1, name: OPERATING_EXPENSES, annualGrowth: DEFAULT_EXPENSE_ANNUAL_GROWTH,
    }];
    const otherIncome = showOtherIncomeToggle ? transformedOtherIncome : [];

    return {
        ...operatingInfoValues,
        occupancyUponStabilizationPercentage: occupancyUponStabilizationPercentage > 0
            ? parseFloat(Number(occupancyUponStabilizationPercentage).toFixed(2)) : 0,
        incomeUnitMixes: transformedUnitArray,
        otherIncome,
        operatingExpenses,
        proFormaStatusId: isDraft ? PRO_FORMA_STATUS.DRAFT : PRO_FORMA_STATUS.IN_PROGRESS,
        draftStep: 3,
    };
}
